import type { Factsheet } from "./types";

// Cross-session factsheet cache used by SpeciesDetail (read-through) and
// the CollectionsPage idle prefetch (write). In-memory primary so reads
// are instant, with a localStorage write-through so reopens after a hard
// reload also skip the round trip. LRU-evict on insert when the persisted
// store would otherwise blow past MAX_ENTRIES.
//
// perf-options F7 pass: added VERSION guard + TTL so stale entries
// self-evict on load. The Factsheet shape has drifted before (sources
// summary, blurb field), which pre-guard would have kept serving old
// shapes silently after a deploy. New: bumped-version load returns
// empty; per-entry expiry drops anything past 60 days.

const KEY = "fish-finder:factsheets";
const VERSION_KEY = "fish-finder:factsheets-version";
const VERSION = 2; // bump when Factsheet type gains / drops a field
const MAX_ENTRIES = 100;
const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

interface CacheEntry {
  v: Factsheet;
  t: number; // unix ms; we sort by this on load to keep evictions FIFO-ish
}

let memory: Map<string, CacheEntry> | null = null;

function load(): Map<string, CacheEntry> {
  if (memory) return memory;
  const m = new Map<string, CacheEntry>();
  try {
    const storedVersion = Number(localStorage.getItem(VERSION_KEY) ?? "0");
    // Version mismatch → discard the whole store (the persisted shape may
    // no longer match Factsheet). Stamp the new version so we don't
    // re-check every load.
    if (storedVersion !== VERSION) {
      localStorage.removeItem(KEY);
      localStorage.setItem(VERSION_KEY, String(VERSION));
      memory = m;
      return m;
    }
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, CacheEntry>;
      const now = Date.now();
      // Restore in age-ascending order so the Map's iteration order matches
      // "oldest first" — the eviction loop in persist() then drops the
      // oldest entries cleanly. Drop anything past TTL up front.
      const sorted = Object.entries(obj)
        .filter(
          ([, e]) =>
            e && typeof e === "object" && "v" in e && "t" in e &&
            now - e.t < TTL_MS,
        )
        .sort(([, a], [, b]) => a.t - b.t);
      for (const [k, v] of sorted) m.set(k, v);
    }
  } catch {
    /* localStorage unavailable / corrupted — keep in-memory only */
  }
  memory = m;
  return m;
}

function persist(): void {
  if (!memory) return;
  while (memory.size > MAX_ENTRIES) {
    const oldest = memory.keys().next().value;
    if (oldest === undefined) break;
    memory.delete(oldest);
  }
  try {
    const obj: Record<string, CacheEntry> = {};
    for (const [k, v] of memory) obj[k] = v;
    localStorage.setItem(KEY, JSON.stringify(obj));
    localStorage.setItem(VERSION_KEY, String(VERSION));
  } catch {
    /* quota exceeded or storage unavailable — keep the in-memory copy */
  }
}

export function factsheetCacheKey(input: {
  taxon_id: number;
  scientific_name: string;
}): string {
  return `${input.taxon_id}|${input.scientific_name.toLowerCase()}`;
}

export function getCachedFactsheet(input: {
  taxon_id: number;
  scientific_name: string;
}): Factsheet | null {
  const m = load();
  const entry = m.get(factsheetCacheKey(input));
  if (!entry) return null;
  // Per-entry TTL guard as well — the load-time filter catches most, but
  // a long-lived tab could hold an entry past TTL until the next reload.
  if (Date.now() - entry.t >= TTL_MS) {
    m.delete(factsheetCacheKey(input));
    return null;
  }
  return entry.v;
}

export function setCachedFactsheet(
  input: { taxon_id: number; scientific_name: string },
  value: Factsheet,
): void {
  const m = load();
  const k = factsheetCacheKey(input);
  // Re-insert to bump to the most-recent position in iteration order.
  m.delete(k);
  m.set(k, { v: value, t: Date.now() });
  persist();
}
